import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { ModalService } from '@app/core/services/modal.service';
import { PrintService } from '@app/core/services/print.service';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import { ChangesCuttingPlanParams, CuttingPlanCompGridRow, CuttingPlanGet, CuttingPlanPoGridRow, CuttingPlanService, FormatItem, GetCuttingPlanParams, StatusItem, UdefProductItem } from '@app/services/cutting-plan.service';
import { EquipmentItem, EquipmentService } from '@app/services/equipment.service';
import { FactoryItem, FactoryService } from '@app/services/factory.service';
import { BusinesscoItem, LogisticService, OperationItem, StockplaceItem } from '@app/services/logistic.service';
import { WorkGroupItem, WorkGroupService } from '@app/services/work-group.service';
import { Subject, takeUntil, groupBy, mergeMap, debounceTime, switchMap, EMPTY, catchError, from } from 'rxjs';

@Component({
    selector: 'tt-cutting-plan',
    templateUrl: './cutting-plan.component.html',
    styleUrls: ['./cutting-plan.component.css'],
})
export class CuttingPlanComponent extends ComponentBaseComponent implements OnInit {
    public params?: GetCuttingPlanParams;

    public get?: CuttingPlanGet;

    public scan?: ErrorMessageResponse;

    public companies: BusinesscoItem[] = [];

    public factories: FactoryItem[] = [];

    public workgroups: WorkGroupItem[] = [];

    public operations: OperationItem[] = [];

    public equipments: EquipmentItem[] = [];

    public formats: FormatItem[] = [];

    public statuses: StatusItem[] = [];

    public stockplaces: StockplaceItem[] = [];

    // TODO: typing
    public printers: any[] = [];

    public poGrid?: GridOptions<CuttingPlanPoGridRow>;

    public compGrid?: GridOptions<CuttingPlanCompGridRow>;

    public plansGrid?: GridOptions;

    public saving = false;

    public printing = false;

    public optimalizing = false;

    public distributing = false;

    public steppingNext = false;

    public steppingPrevious = false;

    public handlingRealConsumption = false;

    public resettingPick = false;

    public finishing = false;

    private destroy$ = new Subject<void>();

    private change$ = new Subject<ChangesCuttingPlanParams>();

    constructor(
        private cuttingPlan: CuttingPlanService,
        private logistic: LogisticService,
        private factory: FactoryService,
        private workgroup: WorkGroupService,
        private equipment: EquipmentService,
        private remember: RememberService,
        private printService: PrintService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        super();
        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.cuttingPlan.changesCuttingPlan(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onChangeCuttingPlanResponse });
    }

    private onChangeCuttingPlanResponse = (response: Partial<CuttingPlanGet>) => {
        if (!!this.get) {
            for (let k in response) {
                let key: keyof CuttingPlanGet = k as keyof CuttingPlanGet;

                this.get[key] = response[key] as any;
            }

            this.readCompGrid();
        }
    };

    public async finish() {
        if (!this.get) return;

        try {
            this.finishing = true;

            const confirmed = await this.modal.openConfirmDialog({ type: 'warning', title: 'cuttingplan_finish_confirm_title', translateTitle: true, message: 'cuttingplan_finish_confirm_message', translateMessage: true });

            if (confirmed) {
                const { errorcode, errormessage } = await this.cuttingPlan.finishCuttingPlan(this.get);

                if ('' + errorcode !== '0') {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            await this.loadGet();
            this.readPoGrid();
            this.readPlansGrid();
            this.readCompGrid();
            this.finishing = false;
        }
    }

    public async resetPick() {
        if (!this.get) return;

        try {
            this.resettingPick = true;

            const { errorcode, errormessage } = await this.cuttingPlan.resetCuttingPlanPick(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            await this.loadGet();
            this.readPoGrid();
            this.readPlansGrid();
            this.readCompGrid();
            this.resettingPick = false;
        }
    }

    public async openRealConsumptionDialog() {
        if (!this.get) return;
        this.handlingRealConsumption = true;

        await this.modal.openStateDialog({ state: 'cuttingplanreal', params: '' + this.get.cuttingplan_keyno });
        await this.loadGet();
        this.readPoGrid();
        this.readPlansGrid();
        this.readCompGrid();

        this.handlingRealConsumption = false;
    }

    public async nextStep() {
        if (!this.get) return;

        try {
            this.steppingNext = true;

            const { errorcode, errormessage } = await this.cuttingPlan.updateToNextStatus(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }

            await this.loadGet();
            this.readPoGrid();
            this.readPlansGrid();
            this.readCompGrid();
        } catch (error) {
            console.error(error);
        } finally {
            this.steppingNext = false;
        }
    }

    public async previousStep() {
        if (!this.get) return;

        try {
            this.steppingPrevious = true;

            const { errorcode, errormessage } = await this.cuttingPlan.updateToPreviousStatus(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }

            await this.loadGet();
            this.readPoGrid();
            this.readPlansGrid();
            this.readCompGrid();
        } catch (error) {
            console.error(error);
        } finally {
            this.steppingPrevious = false;
        }
    }

    public searchProducts = () => {
        return this.cuttingPlan.getUdefProducts({ searchtext: this.get?.prod_name_undefined_format ?? '' });
    };

    public onProductSelected(event?: UdefProductItem) {
        if (!this.get) return;

        this.get.prod_id = event?.prod_id ?? '';
        this.get.prod_name = event?.prod_keyname ?? '';
        // TODO: to remove?
        // this.get.prod_keyname = event?.prod_keyname ?? '';

        this.get.prod_id_undefined_format = event?.prod_id ?? '';
        this.get.prod_name_undefined_format = event?.prod_keyname ?? '';

        this.onChange('prod_id_undefined_format');

        this.remember.remember('w_cuttingplan.prod_id', this.get.prod_id);
        // this.remember.remember('w_cuttingplan.prod_keyname', this.get.prod_keyname);

        this.loadFormats();
    }

    public onComponentAlternativeSelected(event: FormatItem | null) {
        if (!this.get) return;

        this.get.prod_id = event?.prod_id ?? '';
        this.get.prod_name = event?.prod_name ?? '';

        this.onChange('prod_id');
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage } = await this.cuttingPlan.saveCuttingPlan(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }

            await this.loadGet();
            this.readPoGrid();
            this.readCompGrid();
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async print() {
        if (!this.get) return;

        try {
            this.printing = true;

            const { errorcode, errormessage } = await this.cuttingPlan.printCuttingPlan({ cuttingplan_keyno: this.get.cuttingplan_keyno, printer_keyno: this.get.printer_keyno! });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.printing = false;
        }
    }

    public async optimalize() {
        if (!this.get) return;

        try {
            this.optimalizing = true;

            const { errorcode, errormessage, filecontent, filename } = await this.cuttingPlan.getOptimalizedCuttingPlanFile(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                const decoded = this.base64UrlDecode(filecontent);
                const blob = new Blob([decoded], { type: 'text/txt' });
                const url = URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.setAttribute('href', url);
                a.setAttribute('download', filename);
                a.click();
                document.removeChild(a);
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.optimalizing = false;
        }
    }

    public async distribute() {
        if (!this.get) return;

        try {
            this.distributing = true;

            const { errorcode, errormessage } = await this.cuttingPlan.distributeCuttingPlan(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }

            this.readPoGrid();
            this.readCompGrid();
        } catch (error) {
            console.error(error);
        } finally {
            this.distributing = false;
        }
    }

    public onBusinessCoChanged() {
        this.loadFactories();
        this.loadStockplaces();
        this.onChange('businessco_no');
    }

    public onFactoryChanged() {
        this.loadWorkGroups();
        this.loadEquipments();
        this.onChange('factory_id');
    }

    public onWorkGroupChanged() {
        this.loadEquipments();
        this.loadOperations();
        this.onChange('workgrp_keyno');
    }

    public onEquipmentChanged() {
        this.loadOperations();
        this.onChange('equipment_id');
    }

    public onChange(key: keyof CuttingPlanGet) {
        if (!this.get) return;

        this.change$.next({ ...this.get, colname_changed: key });
        this.poGrid?.gridfunc?.refreshToolbarBtnDisability();
    }

    public async onScan() {
        if (!!this.get && this.get.barcode.length > 0) {
            this.scan = await this.cuttingPlan.scanCuttingPlan(this.get);

            this.get.barcode = '';

            if (this.scan.errormessage === '0') {
                await this.loadGet();
                this.readPoGrid();
                this.readCompGrid();
            }
        }
    }

    public readPoGrid() {
        if (!this.poGrid) {
            this.poGrid = new Grid<CuttingPlanPoGridRow>()
                .setRememberId('w_cuttingplan_cuttingplanpo_grid')
                .setLoadDataFunction({ method: 2886, parameters: () => this.get })
                .setSaveDataFunction({ parameters: () => this.get })
                .setKendoConfig({ height: null })
                .addToolbarButton({ name: 'w_cuttingplan_add_po', text: 'w_cuttingplan_add_po', translate: true, icon: 'far fa-plus', cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r', func: this.openCuttingPlanPoAddDialog, disabled: () => (this.get!.cuttingplanstatus_no !== '15' ? 'hidden' : false) })
                .addToolbarButton({ name: 'w_cuttingplan_delete_po', text: 'w_cuttingplan_delete_po', translate: true, icon: 'fad fa-trash', cssClass: 'tt-button tt-button--danger im-grid-btn-xs-r', func: this.deletePoGridRows, disable_if_no_selected: true, disabled: () => (this.get!.cuttingplanstatus_no !== '15' ? 'hidden' : false) });
        } else {
            this.poGrid.gridfunc?.read();
        }
    }

    private deletePoGridRows = async () => {
        const rows = this.poGrid?.gridfunc?.getIsSelectedRows() ?? [];

        if (!this.poGrid || rows.length === 0) return;

        let count = 0;
        let modalClosed = false;
        const progress = this.modal.openProgressDialog({ value: count, max: rows.length, rememberId: !!this.poGrid.dataTask?.rememberId ? this.poGrid.dataTask.rememberId + '.progress' : undefined });

        progress.dialogRef.beforeClosed().subscribe({ next: () => (modalClosed = true) });

        for (let row of rows) {
            try {
                if (modalClosed) break;

                const { errorcode, errormessage } = await this.cuttingPlan.deletePoFromCuttingPlan(row);

                if ('' + errorcode !== '0') {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });

                    progress.updateStatus('invalid');
                    break;
                } else {
                    count++;
                    progress.updateValue(count);
                }

                if (count === rows.length) {
                    progress.updateStatus('finish');
                }
            } catch (error) {
                console.error(error);
                break;
            }
        }

        await this.loadGet();
        this.readPoGrid();
        this.readPlansGrid();
        this.readCompGrid();
    };

    private openCuttingPlanPoAddDialog = async () => {
        if (!this.get) return;

        await this.modal.openStateDialog({ state: 'cuttingplanpoadd', params: '' + this.get.cuttingplan_keyno });
        await this.loadGet();
        this.readPoGrid();
        this.readCompGrid();
    };

    private deletePoGridRow = async (row: CuttingPlanPoGridRow) => {
        const removedRow = await this.poGrid?.gridfunc?.removeRow(row);

        if (!!removedRow) {
            this.poGrid?.gridfunc?.saveChanges(false);
            await this.loadGet();
            this.readCompGrid();
        }
    };

    public readCompGrid() {
        if (!this.compGrid) {
            this.compGrid = new Grid<CuttingPlanCompGridRow>()
                .setRememberId('w_cuttingplan_cuttingplanpocomp_grid')
                .setLoadDataFunction({ method: 2887, parameters: () => this.get })
                .setKendoConfig({ height: null });
        } else {
            this.compGrid.gridfunc?.read();
        }
    }

    public readPlansGrid() {
        if (!this.plansGrid) {
            this.plansGrid = new Grid()
                .setRememberId('w_cuttingplan_cuttingplansb_grid')
                .setLoadDataFunction({ method: 3996, parameters: () => this.get })
                .setKendoConfig({ height: null });
        } else {
            this.plansGrid.gridfunc?.read();
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.cuttingPlan.getCuttingPlan(this.params);
            this.poGrid?.gridfunc?.refreshToolbarBtnDisability();
        }
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    private async loadFactories() {
        if (!!this.get) {
            this.factories = await this.factory.getFactories({ businessco_no: this.get.businessco_no });
        }
    }

    private async loadWorkGroups() {
        if (!!this.get) {
            this.workgroups = await this.workgroup.getWorkGroups({ factory_id: this.get.factory_id });
        }
    }

    private async loadOperations() {
        if (!!this.get) {
            this.operations = await this.logistic.getOperations({ direct: '1', equipment_id: this.get.equipment_id, workgrp_keyno: this.get.workgrp_keyno });
        }
    }

    private async loadEquipments() {
        if (!!this.get) {
            this.equipments = await this.equipment.getEquipments({ factory_id: this.get.factory_id, workgrp_keyno: this.get.workgrp_keyno });
        }
    }

    private async loadFormats() {
        if (!!this.get) {
            this.formats = await this.cuttingPlan.getFormats({ prod_id: this.get.prod_id_undefined_format });
        }
    }

    private async loadStatuses() {
        this.statuses = await this.cuttingPlan.getStatuses();
    }

    private async loadStockplaces() {
        if (!!this.get) {
            this.stockplaces = await this.logistic.getStockplaces({ businessco_no: '' + this.get.businessco_no });
        }
    }

    private async loadPrinters() {
        this.printers = await this.printService.printService.getPrinters();
    }

    private async init() {
        this.loadPrinters();
        this.loadCompanies();
        this.loadStatuses();
        await this.loadGet();
        this.loadFactories();
        this.loadWorkGroups();
        this.loadOperations();
        this.loadEquipments();
        this.loadFormats();
        this.loadStockplaces();
        this.readPoGrid();
        this.readCompGrid();
        this.readPlansGrid();
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.params = this.navigator.params as any;
            this.init();
        }
    }
}
